// action - state management
import { mergeWith } from "lodash";
import {
  APP_SETTINGS_RESET_STATE,
  SET_TRIAL_SUBSCRIPTION,
  SET_USER_LANGUAGE,
  SET_APP_SUB_CONFIG_INITIAL_USER_SETTINGS_STATE,
  SET_MAP_BACKGROUND_LAYER,
  SET_MAP_OVERLAY_LAYERS,
  SET_MAP_CUSTOM_LAYERS,
  SET_MAP_CATEGORIES,
  SET_MAP_CATEGORY_FILTER_STATE,
  SET_LABELS_ACTIVE,
  SET_LABEL_BEHAVIOR,
  SET_TOOLTIPS_ACTIVE,
  SET_FULL_STATE,
  SET_INIT_STATE,
  SET_MAP_DISPLAY_TOTAL_VALUES,
  SET_MAP_DISPLAY_MAP_PERIMETER_FEATURES,
  SET_MAP_DISPLAY_ACTIVITY_NOTIFICATION,
  SET_AUTO_ACTIVATE_ACTIVITY_THEMES,
  SET_SHARE_WITH_ALL_ADMIN_ORGANIZATION,
  SET_PROJECT_SHARE_WITH_ALL_ADMIN_ORGANIZATION,
  SET_CONTACT_COMMUNICATION_SETTINGS,
  SET_USE_SYSTEM_INVENTORY_DISPLAY_REFERENCE,
  SET_FEATURE_CREATION_PREFERENCE,
} from "./actions";

// Utility
import { AppSettingsActionProps, AppSettingsProps } from "types/appSettings";
import { updateObject } from "utils/updateObject";

// Initial state
const initialState: AppSettingsProps = {
  initializing: true,
  trialSubscription: "unknown",
  userLanguage: null,
  appSubConfigInitialUserSettingsState: "not-initialized",
  map: {
    backgroundLayer: "googleTerrain",
    overlayLayers: null,
    customLayers: null,
    categories: null,
    categoryFilterState: null,
    displayTotalValues: false,
    pointLabelsActive: true,
    lineLabelsActive: false,
    labelBackgroundActive: false,
    labelZoomDependenceActive: true,
    polygonLabelsActive: true,
    pointTooltipsActive: true,
    lineTooltipsActive: true,
    polygonTooltipsActive: true,
    displayMapPerimeterFeatures: false,
    displayActivityNotifications: false,
    autoActivateActivityThemes: null,
  },
  sharedata: {
    mapShareWithAllAdminOrganization: false,
    projectShareWithAllAdminOrganization: false,
  },
  contactCommunication: {
    sharing: true,
    subscriptionChanges: true,
    buyerSellerApprovalFlow: true,
    activityResponsible: true,
    noteMentions: true,
  },
  inventory: {
    useSystemDisplayReference: true,
  },
  featureCreation: {
    version: 1,
    preferences: {},
  },
};

const appSettings = (
  state: AppSettingsProps = initialState,
  action: AppSettingsActionProps,
): AppSettingsProps => {
  const resetState = () => {
    return initialState;
  };

  const setTrialSubscription = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, { trialSubscription: action.payload });
  };

  const setUserLanguage = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, { userLanguage: action.payload });
  };

  const setAppSubConfigInitialUserSettingsState = (
    state: AppSettingsProps,
    action: AppSettingsActionProps,
  ) => {
    return updateObject(state, { appSubConfigInitialUserSettingsState: action.payload });
  };

  const setMapBackgroundLayer = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      map: {
        ...state.map,
        backgroundLayer: action.payload,
      },
    });
  };

  const setMapOverlayLayers = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      map: {
        ...state.map,
        overlayLayers: action.payload,
      },
    });
  };

  const setMapCustomLayers = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      map: {
        ...state.map,
        customLayers: action.payload,
      },
    });
  };

  const setMapCategories = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      map: {
        ...state.map,
        categories: action.payload,
      },
    });
  };

  const setMapCategoryFilterState = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      map: {
        ...state.map,
        categoryFilterState: action.payload,
      },
    });
  };

  const setMapDisplayTotalValues = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      map: {
        ...state.map,
        displayTotalValues: action.payload,
      },
    });
  };

  const setLabelsActive = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      map: {
        ...state.map,
        ...action.payload,
      },
    });
  };

  const setLabelBehavior = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      map: {
        ...state.map,
        ...action.payload,
      },
    });
  };

  const setTooltipsActive = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      map: {
        ...state.map,
        ...action.payload,
      },
    });
  };

  const setMapDisplayMapPerimeterFeatures = (
    state: AppSettingsProps,
    action: AppSettingsActionProps,
  ) => {
    return updateObject(state, {
      map: {
        ...state.map,
        displayMapPerimeterFeatures: action.payload,
      },
    });
  };

  const setMapDisplayActivityNotification = (
    state: AppSettingsProps,
    action: AppSettingsActionProps,
  ) => {
    return updateObject(state, {
      map: {
        ...state.map,
        displayActivityNotifications: action.payload,
      },
    });
  };

  const setMapShareWithAllAdminOrganization = (
    state: AppSettingsProps,
    action: AppSettingsActionProps,
  ) => {
    return updateObject(state, {
      sharedata: {
        ...state.sharedata,
        mapShareWithAllAdminOrganization: action.payload,
      },
    });
  };

  const setProjectShareWithAllAdminOrganization = (
    state: AppSettingsProps,
    action: AppSettingsActionProps,
  ) => {
    return updateObject(state, {
      sharedata: {
        ...state.sharedata,
        projectShareWithAllAdminOrganization: action.payload,
      },
    });
  };

  const setFullState = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    const newState = mergeWith({}, state, action.payload);
    return newState;
  };

  const setContactCommunicationSettings = (
    state: AppSettingsProps,
    settings: Partial<AppSettingsProps["contactCommunication"]>,
  ) =>
    updateObject(state, {
      contactCommunication: { ...state.contactCommunication, ...settings },
    });

  const setUseSystemInventoryDisplayReference = (
    state: AppSettingsProps,
    enabled: boolean,
  ) => updateObject(state, { inventory: { useSystemDisplayReference: enabled } });

  const setInitState = (state: AppSettingsProps, action: AppSettingsActionProps) => {
    return updateObject(state, {
      initializing: action.payload,
    });
  };

  switch (action.type) {
    case APP_SETTINGS_RESET_STATE:
      return resetState();
    case SET_TRIAL_SUBSCRIPTION:
      return setTrialSubscription(state, action);

    case SET_USER_LANGUAGE:
      return setUserLanguage(state, action);
    case SET_APP_SUB_CONFIG_INITIAL_USER_SETTINGS_STATE:
      return setAppSubConfigInitialUserSettingsState(state, action);
    case SET_MAP_BACKGROUND_LAYER:
      return setMapBackgroundLayer(state, action);
    case SET_MAP_OVERLAY_LAYERS:
      return setMapOverlayLayers(state, action);
    case SET_MAP_CUSTOM_LAYERS:
      return setMapCustomLayers(state, action);

    case SET_MAP_CATEGORIES:
      return setMapCategories(state, action);
    case SET_MAP_CATEGORY_FILTER_STATE:
      return setMapCategoryFilterState(state, action);

    case SET_MAP_DISPLAY_TOTAL_VALUES:
      return setMapDisplayTotalValues(state, action);
    case SET_LABELS_ACTIVE:
      return setLabelsActive(state, action);
    case SET_LABEL_BEHAVIOR:
      return setLabelBehavior(state, action);
    case SET_TOOLTIPS_ACTIVE:
      return setTooltipsActive(state, action);
    case SET_MAP_DISPLAY_MAP_PERIMETER_FEATURES:
      return setMapDisplayMapPerimeterFeatures(state, action);
    case SET_AUTO_ACTIVATE_ACTIVITY_THEMES:
      return updateObject(state, {
        map: { ...state.map, autoActivateActivityThemes: action.payload },
      });
    case SET_MAP_DISPLAY_ACTIVITY_NOTIFICATION:
      return setMapDisplayActivityNotification(state, action);

    case SET_SHARE_WITH_ALL_ADMIN_ORGANIZATION:
      return setMapShareWithAllAdminOrganization(state, action);
    case SET_PROJECT_SHARE_WITH_ALL_ADMIN_ORGANIZATION:
      return setProjectShareWithAllAdminOrganization(state, action);
    case SET_CONTACT_COMMUNICATION_SETTINGS:
      return setContactCommunicationSettings(state, action.payload);
    case SET_USE_SYSTEM_INVENTORY_DISPLAY_REFERENCE:
      return setUseSystemInventoryDisplayReference(state, action.payload);
    case SET_FEATURE_CREATION_PREFERENCE: {
      // Fast Refresh can preserve an app-settings state created before this
      // optional persisted setting existed. Normalize it before updating.
      const featureCreation = state.featureCreation ?? initialState.featureCreation;
      return updateObject(state, {
        featureCreation: {
          ...featureCreation,
          preferences: {
            ...featureCreation.preferences,
            [action.payload.context]: {
              ...featureCreation.preferences[action.payload.context],
              ...action.payload.preference,
            },
          },
        },
      });
    }

    case SET_FULL_STATE:
      return setFullState(state, action);

    case SET_INIT_STATE:
      return setInitState(state, action);

    default:
      return state;
  }
};

export default appSettings;
